import { Button, Flex } from '@mantine/core';
import { useMemo } from 'react';
import { MipClassifierRes } from '@dealstack/shared/lib/generated';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { LabeledSegmentedControl } from '@dealstack/frontend/shared/components/LabeledSegmentedControl/LabeledSegmentedControl.tsx';
import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState.tsx';
import { PermissionWrapper } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { ArchivedFilter } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/utils/types';
import { useClassifiersTableState } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/components/ClassifiersTable/useClassifiersTableState.ts';

interface Props {
	classifiers: MipClassifierRes[];
	isLoading: boolean;
	canEdit: boolean;
	archivedFilter: ArchivedFilter;
	onArchivedFilterChange: (value: ArchivedFilter) => void;
	onEdit: (classifier: MipClassifierRes) => void;
	onAdd: () => void;
	onArchiveToggle: (classifier: MipClassifierRes) => void;
	onManageTags: (classifier: MipClassifierRes) => void;
	onRemoveTag: (cohortId: string, tagId: string) => void;
	tagsByClassifierId: Record<string, { id: string; name: string }[]>;
}

export const ClassifiersTable = ({
	classifiers,
	isLoading,
	canEdit,
	archivedFilter,
	onArchivedFilterChange,
	onEdit,
	onAdd,
	onArchiveToggle,
	onManageTags,
	onRemoveTag,
	tagsByClassifierId,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const { columns, mantineTableDataOperations } = useClassifiersTableState({
		onEdit,
		onArchiveToggle,
		onManageTags,
		onRemoveTag,
		canEdit,
		tagsByClassifierId,
	});
	const { mipId } = useRequiredParams();
	const filterOptions: Array<{ label: string; value: ArchivedFilter }> = useMemo(
		() => [
			{ label: tMipManage('root.classifiers.filter.active'), value: 'active' },
			{ label: tMipManage('root.classifiers.filter.archived'), value: 'archived' },
			{ label: tMipManage('root.classifiers.filter.all'), value: 'all' },
		],
		[tMipManage]
	);

	return (
		<Flex direction="column" w="100%" h="100%" p="1rem" gap="1rem">
			<Flex justify="flex-end" align="center" gap="0.5rem">
				<LabeledSegmentedControl<ArchivedFilter>
					value={archivedFilter}
					onChange={onArchivedFilterChange}
					data={filterOptions}
					label={tMipManage('root.classifiers.filter.label')}
				/>
				<PermissionWrapper
					resourceType={ResourceType.PROGRAMME}
					resourceId={mipId}
					permissionRole={PermissionRole.ADMIN}
				>
					<Button onClick={onAdd} leftSection={<Icon variant="plus" size="lg" />}>
						{tMipManage('root.classifiers.add_button')}
					</Button>
				</PermissionWrapper>
			</Flex>
			{!isLoading && !classifiers.length ? (
				<EmptyState
					title={tMipManage('root.classifiers.empty_state.title')}
					icon="es-person-boxes"
				/>
			) : (
				<MantineTable<MipClassifierRes>
					getRowId={(row) => row.id}
					columns={columns}
					isLoading={isLoading}
					dataOperations={mantineTableDataOperations}
					data={classifiers}
					enableRowActions={false}
					enablePagination={false}
					enableTopToolbar={false}
					enableFilters={false}
					enableGlobalFilter={false}
					enableRowSelection={false}
					manualPagination={false}
					manualSorting={false}
					mantineTableBodyRowProps={() => ({
						style: { height: 'unset' },
					})}
				/>
			)}
		</Flex>
	);
};
